/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import ReactDatePickerRaw from 'react-datepicker'
import type { ReactDatePickerProps } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { getDateTimeFnsParseFormats } from '~/utils/dateFormatting'

// Re-export the raw type so callers can extend it if needed
export type { ReactDatePickerProps }

/**
 * Props for DateTimePicker — identical to react-datepicker props except
 * `dateFormat` is explicitly typed as `string | string[]`.
 *
 * react-datepicker's TypeScript definitions narrow `dateFormat` to `string`
 * at runtime due to `defaultProps`, even though the implementation supports
 * arrays for multi-format typed input parsing. This wrapper corrects that.
 */
export interface DateTimePickerProps extends Omit<ReactDatePickerProps, 'dateFormat'> {
  dateFormat?: string | string[]
}

// Cast to any to bypass TypeScript's defaultProps narrowing of dateFormat to string.
// At runtime react-datepicker v8.9.0 accepts string[] for multi-format parsing.
const ReactDatePicker = ReactDatePickerRaw as React.ComponentType<any>

/**
 * DateTimePicker: a react-datepicker wrapper for components that need time
 * input (`showTimeInput` / `showTimeSelect`) and multi-format typed input parsing.
 *
 * - Defaults `dateFormat` to `getDateTimeFnsParseFormats()` so a user can type
 *   any common date/time format (e.g. "13/05/2026 12:00:00 PM", "12.05.2026 14:00")
 *   regardless of the app's configured display format.
 * - For date-only pickers use CustomDatePicker (lego) instead.
 */
const DateTimePicker: React.FC<DateTimePickerProps> = ({ dateFormat, ...rest }) => {
  const resolvedFormat = dateFormat ?? getDateTimeFnsParseFormats()
  return <ReactDatePicker dateFormat={resolvedFormat} {...rest} />
}

export default DateTimePicker
